import React, { useMemo, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { DrawerItemProps } from '@progress/kendo-react-layout';
import { useLocalization } from '@progress/kendo-react-intl';
import moment from 'moment';
import { enMessages } from '../../../../../messages/en-US';
import NoteCard, { InternalChip } from './NoteCard';
import { useNoteCategories, useNoteEditing } from './noteHooks';
import EntityNoteForm from './EntityNoteForm';
import { getEntityNoteIcon } from './entityNoteIcon';
import { EntityNoteCategory } from './noteCategories';

/**
 * Backend-defined entity types that can have notes attached.
 *
 * `Point` exists in the backend enum but is not used in the asset dashboard —
 * all "lubrication points" rendered on this dashboard are tagged as `Endpoint`.
 *
 * `Device` covers gateways (the backend dropped the dedicated `Gateway` type).
 * Use `Asset` (not `Machine`) and `Location` (not `Area`) — the backend translates internally and returns the UI value.
 *
 * `Alert` attaches notes to a triggered alert row (Overview → Alerts popover); `Notification` to a
 * notifications-table row. The backend doesn't serve `web/v1/notes/Alert|Notification/{id}` yet —
 * confirm the exact entityType tokens when they ship.
 */
export type EntityType =
  | 'Asset'
  | 'Point'
  | 'Endpoint'
  | 'GreasePump'
  | 'Device'
  | 'Location'
  | 'Alert'
  | 'AlertNotification';

// Canonical category keys live in ./noteCategories — re-exported here so existing import paths keep working.
export type { EntityNoteCategory } from './noteCategories';

/**
 * The single, normalized note shape used everywhere in the UI. The raw backend payload
 * (`comment`/`user`/`timestamp`) is mapped to this inside `NotesApiService` and never leaks out.
 * Per-entity endpoints leave `assetName` undefined; the site-wide rollup fills it in.
 */
export interface Note {
  id: string;
  entityType: EntityType;
  entityId: string;
  /** Display name of the entity, snapshotted at write time. "" when the backend has none. */
  entityName: string;
  /** Extra entity context, e.g. `{ serialNo, deviceType }` for Endpoint/Device. `{}` when empty, never null. */
  entityMetadata: Record<string, string>;
  description: string;
  /** A note can be tagged with multiple categories. */
  categories: EntityNoteCategory[];
  /** GUID of the author (JWT `sub`). Used for ownership comparison (edit/delete visibility). */
  userId: string;
  /** Display name snapshot of the author at write time. */
  createdBy: string;
  createdAt: Date;
  /** Visible to internal (GreaseBoss) users only. Maps to the backend `isInternal` flag. */
  isInternalOnly?: boolean;
  /** Whether the note's author is a GreaseBoss (internal) user. Backend `isGbUser`. */
  isGbUser?: boolean;
  /** Owning asset's name — only set on site-wide rollup rows (GET web/v1/notes/assets). */
  assetName?: string;
}

export type EntityNoteInput = {
  description: string;
  categories: EntityNoteCategory[];
  /** Visible to internal (GreaseBoss) users only. Set by internal authors. */
  isInternalOnly?: boolean;
};

export type EntityNotesPanelItemProps = DrawerItemProps & {
  entityType: EntityType;
  entityId: string;
  /** Display name of the entity (e.g. asset name, point name, gateway name) shown in the panel header. */
  entityName?: string;
  /** Optional serial number — when present, shown in parentheses next to the entity name in the header. */
  entitySerial?: string;
  /** Overrides the header icon (e.g. a criticality chevron); falls back to the entity-type icon. */
  headerIcon?: React.ReactNode;
  notes: Note[];
  /** Total number of notes on the entity (may exceed `notes.length` when only the recent subset is loaded). */
  noteCount: number;
  /** GUID of the current user — drives edit/delete visibility on each note. */
  currentUserId: string;
  /** Display name of the current user — used when search-matching against `createdBy`. */
  currentUserName: string;
  onAdd: (note: EntityNoteInput) => void;
  onEdit: (id: string, note: EntityNoteInput) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
};

export function EntityNotesPanelItem(props: EntityNotesPanelItemProps) {
  const { entityType, entityName, entitySerial, headerIcon, notes, noteCount, currentUserId, onAdd, onEdit, onDelete, onClose } = props;
  const localizationService = useLocalization();

  const notesL = localizationService.toLanguageString('custom.notes', enMessages.custom.notes);
  const categoryL = localizationService.toLanguageString('custom.category', enMessages.custom.category);
  const selectCategoryL = localizationService.toLanguageString('custom.selectCategory', enMessages.custom.selectCategory);
  const descriptionL = localizationService.toLanguageString('custom.description', enMessages.custom.description);
  const addNoteButtonL = localizationService.toLanguageString('custom.addNoteButton', enMessages.custom.addNoteButton);
  const addNotePlaceholderL = localizationService.toLanguageString('custom.addNotePlaceholder', enMessages.custom.addNotePlaceholder);
  const noNotesYetL = localizationService.toLanguageString('custom.noNotesYetAddFirst', enMessages.custom.noNotesYetAddFirst);
  const createdByL = localizationService.toLanguageString('custom.createdBy', enMessages.custom.createdBy);
  const searchNotesL = localizationService.toLanguageString('custom.searchNotes', enMessages.custom.searchNotes);
  const noNotesMatchL = localizationService.toLanguageString('custom.noNotesMatchSearch', enMessages.custom.noNotesMatchSearch);
  const editL = localizationService.toLanguageString('custom.edit', enMessages.custom.edit);
  const deleteL = localizationService.toLanguageString('custom.delete', enMessages.custom.delete);
  const cancelL = localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel);
  const saveL = localizationService.toLanguageString('custom.save', enMessages.custom.save);

  // Localized categories + the inline edit/delete state machine — shared with the timeline tabs.
  const { categories, categoryLabel, renderCategoryItem } = useNoteCategories();
  const editing = useNoteEditing({ categories, notes, onSave: onEdit, onDelete });

  const [searchQuery, setSearchQuery] = useState('');

  // Search matches every field shown on a card: description, author, category labels and the date.
  const visibleNotes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => {
      const categoryText = n.categories
        .map((c) => categories.find((o) => o.value === c)?.text ?? c)
        .join(' ');
      const dateText = moment(n.createdAt).format('DD MMM YYYY HH:mm');
      const hay = `${n.description} ${n.createdBy} ${categoryText} ${dateText}`.toLowerCase();
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, searchQuery]);

  const cardLabels = { createdBy: createdByL, edit: editL, delete: deleteL, cancel: cancelL, save: saveL };
  const formLabels = {
    title: addNoteButtonL,
    category: categoryL,
    categoryPlaceholder: selectCategoryL,
    description: descriptionL,
    addNoteButton: addNoteButtonL,
    addNotePlaceholder: addNotePlaceholderL,
  };

  return (
    <div className="flex h-screen max-h-screen min-h-0 min-w-0 flex-col overflow-hidden bg-white">
      {/* Header — entity-type icon + name (+ serial when present). */}
      <div className="shrink-0 border-b border-gray-200 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="inline-flex shrink-0 items-center justify-center">
              {headerIcon ?? getEntityNoteIcon(entityType, notes[0]?.entityMetadata?.deviceType)}
            </span>
            <div className="min-w-0 flex-1">
              <div
                className="truncate text-base font-semibold leading-snug"
                title={entitySerial ? `${entityName} (${entitySerial})` : entityName}
              >
                {entityName || notesL}
                {entitySerial && <span className="ml-1 text-sm font-normal opacity-70">({entitySerial})</span>}
              </div>
            </div>
          </div>
          <Button fillMode="flat" themeColor="base" onClick={onClose} aria-label="Close panel">
            ✕
          </Button>
        </div>
      </div>

      <EntityNoteForm
        categories={categories}
        onAdd={onAdd}
        renderCategoryItem={renderCategoryItem}
        labels={formLabels}
      />

      {/* List section title — sits on the gray inset above the search + cards. */}
      {notes.length > 0 && (
        <div className="shrink-0 bg-[#fafafa] px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {notesL} ({noteCount})
        </div>
      )}

      {/* Search — sits on the same gray inset as the list below it. */}
      {notes.length > 0 && (
        <div className="shrink-0 bg-[#fafafa] px-3 pb-1 pt-2">
          <div className="relative">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.value as string)}
              placeholder={searchNotesL}
              style={{ width: '100%' }}
            />
            {searchQuery && (
              <Button
                fillMode="flat"
                themeColor="base"
                onClick={() => setSearchQuery('')}
                className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !p-0.5"
                aria-label="Clear search"
              >
                ✕
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Notes list — gray inset with white note cards, matching the dashboard timeline tone. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-[#fafafa] p-3 pt-1">
        {notes.length === 0 ? (
          <div className="flex flex-1 items-center justify-center px-4 py-6 text-center text-sm">
            {noNotesYetL}
          </div>
        ) : visibleNotes.length === 0 ? (
          <div className="flex flex-1 items-center justify-center px-4 py-6 text-center text-sm">
            {noNotesMatchL}
          </div>
        ) : (
          visibleNotes.map((note) => {
            const isOwn = note.userId === currentUserId && currentUserId.length > 0;
            return (
              <NoteCard
                key={note.id}
                note={note}
                className="shrink-0"
                header={
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs">{moment(note.createdAt).format('DD MMM YYYY HH:mm')}</span>
                    {note.isInternalOnly && <InternalChip />}
                  </div>
                }
                categories={categories}
                categoryLabel={categoryLabel}
                isOwn={isOwn}
                authorIsInternal={!!note.isGbUser}
                isBeingEdited={editing.editingId === note.id}
                editingDescription={editing.editingDescription}
                editingCategories={editing.editingCategories}
                editingInternalOnly={editing.editingInternalOnly}
                onChangeEditingDescription={editing.setEditingDescription}
                onChangeEditingCategories={editing.setEditingCategories}
                onChangeEditingInternalOnly={editing.setEditingInternalOnly}
                onStartEdit={() =>
                  editing.startEdit({
                    id: note.id,
                    description: note.description,
                    categories: note.categories,
                    isInternalOnly: note.isInternalOnly,
                  })
                }
                onCancelEdit={editing.cancelEdit}
                onSaveEdit={editing.saveEdit}
                onDelete={() => editing.deleteNote(note.id)}
                renderCategoryItem={renderCategoryItem}
                labels={cardLabels}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
